import { useState } from "react";
import { Search, Loader2, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchMessagesParams } from "@/lib/api/admin";

interface SearchFormProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchOptions: Partial<SearchMessagesParams>;
  onSearchOptionsChange: (options: Partial<SearchMessagesParams>) => void;
  onSearch: () => void;
  isSearching: boolean;
}

export function SearchForm({
  searchQuery,
  setSearchQuery,
  searchOptions,
  onSearchOptionsChange,
  onSearch,
  isSearching,
}: SearchFormProps) {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  return (
    <Card className="border-0 bg-gradient-to-br from-white to-gray-50/50 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900">
          <div className="rounded-lg bg-blue-100 p-2">
            <Search className="h-5 w-5 text-blue-600" />
          </div>
          Search Messages
        </CardTitle>
        <p className="mt-1 text-sm text-gray-600">
          Find specific messages in this conversation
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Search Input */}
        <div className="relative">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <Input
                type="search"
                placeholder="Search for messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>
            <Button
              onClick={onSearch}
              disabled={!searchQuery.trim() || isSearching}
            >
              {isSearching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </div>

        {/* Advanced Options */}
        <div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-600 hover:text-gray-900"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          >
            <Filter className="mr-2 h-4 w-4" />
            Advanced Options
            <div className="ml-auto">
              {showAdvancedOptions ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          </Button>
          {showAdvancedOptions && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Sort by
                  </Label>
                  <Select
                    value={searchOptions.sort_field}
                    onValueChange={(value: "score" | "ts") =>
                      onSearchOptionsChange({ sort_field: value })
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="score">Relevance</SelectItem>
                      <SelectItem value="ts">Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Results per page
                  </Label>
                  <Select
                    value={searchOptions.limit?.toString()}
                    onValueChange={(value) =>
                      onSearchOptionsChange({ limit: parseInt(value) })
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="exact_match"
                    checked={searchOptions.exact_match || false}
                    onCheckedChange={(checked) =>
                      onSearchOptionsChange({
                        exact_match: checked as boolean,
                      })
                    }
                    className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                  />
                  <Label
                    htmlFor="exact_match"
                    className="cursor-pointer text-sm text-gray-700"
                  >
                    Exact match
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="reverse"
                    checked={searchOptions.reverse || false}
                    onCheckedChange={(checked) =>
                      onSearchOptionsChange({
                        reverse: checked as boolean,
                      })
                    }
                    className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                  />
                  <Label
                    htmlFor="reverse"
                    className="cursor-pointer text-sm text-gray-700"
                  >
                    Reverse order
                  </Label>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
